import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ValidationMessageKeys } from 'src/Configuration/validation/translation-keys'
import * as z from 'zod'
import { RepeatOptions } from '../types'
import { isToday, isAfter, startOfDay, setSeconds, setMilliseconds } from '@e3dc-react/shell/Libs/date'
import { ALL_DAYS, Days } from '@e3dc-react/shell/Libs/date/types'

interface ReturnType {
  chargingScheduleFormSchema: z.Schema
}

const SCHEDULE_NAME_LIMIT = 80
const TARGET_STATE_OF_CHARGE_MIN = 0
const TARGET_STATE_OF_CHARGE_MAX = 100

/**
 * Hook to validate "charging schedule form" with zod
 */
export const useValidateChargingScheduleForm = (isEditing: boolean): ReturnType => {
  const { t } = useCustomTranslation()
  const requiredMessage = t(ValidationMessageKeys.required)

  const chargingScheduleFormSchema = z
    .object({
      scheduleName: z
        .string()
        .max(SCHEDULE_NAME_LIMIT, { message: t(ValidationMessageKeys.lengthMax, { value: SCHEDULE_NAME_LIMIT }) })
        .or(z.literal(''))
        .nullable(),
      targetStateOfCharge: z.number({ invalid_type_error: requiredMessage }).min(TARGET_STATE_OF_CHARGE_MIN).max(TARGET_STATE_OF_CHARGE_MAX),
      // startDate must not be in the past unless editing an existing schedule
      startDate: z.date({ message: requiredMessage }).refine(
        date => {
          // Check for valid date object
          if (!(date instanceof Date) || isNaN(date.getTime())) return false
          // Allow any date if editing (for historical schedules)
          if (isEditing) return true
          // Otherwise, only allow today or future dates
          return !isAfter(startOfDay(new Date()), date)
        },
        { message: t('emobility.vehicle-integration.charging-schedule.validation.date-in-the-past') }
      ),
      // startTime must not be in the past if startDate is today and not editing
      startTime: z.date({ message: requiredMessage }),
      enabled: z.boolean(),
      endDate: z.date().nullable(),
      repeat: z.enum(Object.values(RepeatOptions) as [string, ...string[]]),
      selectedWeekDays: z.object(Object.fromEntries(ALL_DAYS.map(day => [day, z.boolean()])) as Record<keyof Days, z.ZodBoolean>),
    })
    // at least one day must be selected if repeat is daily or weekly
    .refine(
      data => {
        if (data.repeat === RepeatOptions.daily || data.repeat === RepeatOptions.weekly) {
          return Object.values(data.selectedWeekDays).some(Boolean)
        }
        return true
      },
      {
        message: requiredMessage,
        path: ['selectedWeekDays'],
      }
    )
    .refine(
      data => {
        // Validate endDate for recurring schedules
        if (data.repeat === RepeatOptions.daily || data.repeat === RepeatOptions.weekly) {
          return data.endDate instanceof Date && !isNaN(data.endDate.getTime())
        }
        // For doNotRepeat, endDate can be null
        return true
      },
      {
        message: requiredMessage,
        path: ['endDate'],
      }
    )
    .refine(
      data => {
        // If editing, skip this validation
        if (isEditing) return true
        // Validate startTime is not in the past if startDate is today
        if (!(data.startDate instanceof Date) || isNaN(data.startDate.getTime())) return true
        if (!(data.startTime instanceof Date) || isNaN(data.startTime.getTime())) return true
        if (isToday(data.startDate)) {
          // startTime must be >= now (rounded down to minute)
          const nowTime = setMilliseconds(setSeconds(new Date(), 0), 0)
          return isAfter(data.startTime, nowTime) || data.startTime.getTime() === nowTime.getTime()
        }
        return true
      },
      {
        message: t('emobility.vehicle-integration.charging-schedule.validation.time-in-the-past'),
        path: ['startTime'],
      }
    )

  return { chargingScheduleFormSchema }
}
